/** @jsxImportSource react */
import { useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, useMediaQuery, useTheme } from '@mui/material'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import { filesAPI } from '../../api/crm'
import { downloadBlob } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'
import PdfPreview from '../documents/PdfPreview'

/** A record's PDF or image shown in the page (full screen on phones), with Download. */
const FilePreviewDialog = ({ recordId, file, onClose }) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [blob, setBlob] = useState(null)
  const [imageUrl, setImageUrl] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!file) return undefined
    let cancelled = false
    let url = null
    setBlob(null); setImageUrl(null); setError(null)
    filesAPI.content(recordId, file.id, true)
      .then((data) => {
        if (cancelled) return
        const typed = new Blob([data], { type: file.contentType })
        setBlob(typed)
        if (file.contentType.startsWith('image/')) setImageUrl((url = URL.createObjectURL(typed)))
      })
      .catch((e) => { if (!cancelled) setError(getErrorMessage(e)) })
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url) }
  }, [recordId, file])

  return (
    <Dialog open={!!file} onClose={onClose} maxWidth="lg" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ pr: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file?.fileName}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{error}</Alert>}
        {!error && !blob && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}
        {blob && file?.contentType === 'application/pdf' && <PdfPreview blob={blob} height={720} title={file.fileName} />}
        {imageUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box component="img" src={imageUrl} alt={file.fileName} sx={{ maxWidth: '100%', maxHeight: { xs: 'none', sm: '75vh' }, objectFit: 'contain' }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button startIcon={<DownloadOutlinedIcon />} disabled={!blob} onClick={() => downloadBlob(blob, file.fileName)}>Download</Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default FilePreviewDialog
